import {
  AdminWorkspaceSchema,
  AskAnswerSchema,
  CaseBriefSchema,
  PublicReviewListSchema,
  QualityOverviewSchema,
  QualityRunSchema,
  DoctrineListSchema,
  DoctrineRecordSchema,
  ReceivedLawListSchema,
  ReceivedLawRecordSchema,
  type DoctrineInput,
  type ReceivedLawInput,
  KeyPointListSchema,
  KeyPointSetSchema,
  type DraftKeyPointsRequest,
  type KeyPointList,
  type GradeRequest,
  type QualityOverview,
  type QualityRun,
  FeedbackListSchema,
  FeedbackSchema,
  type CreateFeedbackRequest,
  type Feedback,
  type FeedbackList,
  OperationsReportSchema,
  type OperationsReport,
  SourceReportListSchema,
  SourceReportSchema,
  type CreateSourceReportRequest,
  type ResolveSourceReportRequest,
  type SourceReport,
  type SourceReportList,
  MemoListSchema,
  MemoSchema,
  type CreateMemoRequest,
  type Memo,
  type MemoList,
  type MemoVisibility,
  ReviewDetailSchema,
  ReviewListSchema,
  type PublicReviewList,
  type ReviewDecision,
  type ReviewDetail,
  type ReviewList,
  LiveQuizSchema,
  type LiveQuiz,
  type CaseBrief,
  ChatListSchema,
  ChatSchema,
  type Chat,
  type ChatList,
  CaseCatalogueSchema,
  CaseSearchSchema,
  CiteCheckResultSchema,
  MatterListSchema,
  MatterSchema,
  MatterDocumentListSchema,
  MatterDocumentSchema,
  ResearchDocumentListSchema,
  ResearchDocumentSchema,
  type ResearchDocument,
  type ResearchDocumentList,
  type MatterDocument,
  type MatterDocumentList,
  type Matter,
  type MatterIntake,
  type MatterList,
  type CiteCheckResult,
  type CaseSearch,
  type CaseCatalogue,
  type AskAnswer,
  AuditEventSchema,
  DirectorySchema,
  FeatureFlagsSchema,
  HealthSchema,
  LibraryCatalogueSchema,
  LibraryVersionSchema,
  MeSchema,
  OrganizationSchema,
  RoleSchema,
  WorkspaceMemberSchema,
  type AdminWorkspace,
  type ApiResponse,
  type AuditEvent,
  type CreateOrganizationRequest,
  type CreateWorkspaceRequest,
  type Directory,
  type FeatureFlags,
  type GrantMembershipRequest,
  type Health,
  type LibraryCatalogue,
  type LibraryVersion,
  type Me,
  type Organization,
  type WorkspaceMember,
} from '@buakei/types';
import { z } from 'zod';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly requestId?: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised when the server refuses the session even after a token refresh. */
export class SessionEndedError extends ApiError {
  constructor(message: string, requestId?: string) {
    super(message, requestId, 401);
    this.name = 'SessionEndedError';
  }
}

/** How protected calls obtain a Firebase ID token (see auth/identity-client.ts). */
export type TokenSource = { getIdToken(forceRefresh?: boolean): Promise<string | null> };

type Method = 'GET' | 'POST' | 'DELETE';

/** A file sent as the request body (client documents, ADR-044), with its own headers. */
class RawBody {
  constructor(
    readonly file: Blob,
    readonly headers: Record<string, string>,
  ) {}
}

async function send(
  method: Method,
  path: string,
  token: string | null,
  body: unknown,
  idempotencyKey: string | null = null,
): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;
  if (body instanceof RawBody) Object.assign(headers, body.headers);
  else if (body !== undefined) headers['content-type'] = 'application/json';
  try {
    return await fetch(path, {
      method,
      headers,
      body:
        body === undefined ? undefined : body instanceof RawBody ? body.file : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      'Could not reach the Buakei AI service. Check your connection and try again.',
    );
  }
}

/**
 * Typed call to the Worker API. Responses are schema-validated, never trusted blindly.
 * With a token source, a 401 triggers exactly one forced token refresh and retry; a second
 * 401 means the session has ended (expired, revoked or disabled account).
 */
async function request<S extends z.ZodType>(
  method: Method,
  path: string,
  schema: S,
  tokens?: TokenSource,
  body?: unknown,
): Promise<z.infer<S>> {
  // AI requests carry one key across resends, so the server never charges one twice (ADR-022).
  const key =
    method === 'POST' &&
    (path.startsWith('/api/v1/assistant/') ||
      /\/matters\/[^/]+\/(?:plan|drafts|drafts\/[^/]+\/revisions)$/.test(path))
      ? crypto.randomUUID().replace(/-/g, '')
      : null;
  let response = await send(method, path, tokens ? await tokens.getIdToken() : null, body, key);
  if (response.status === 401 && tokens) {
    const fresh = await tokens.getIdToken(true).catch(() => null);
    if (fresh) response = await send(method, path, fresh, body, key);
  }
  let payload: ApiResponse<unknown>;
  try {
    payload = (await response.json()) as ApiResponse<unknown>;
  } catch {
    throw new ApiError('The service returned an unexpected response.', undefined, response.status);
  }
  if ('error' in payload) {
    if (response.status === 401 && tokens) {
      throw new SessionEndedError(payload.error.message, payload.requestId);
    }
    throw new ApiError(payload.error.message, payload.requestId, response.status);
  }
  const parsed = schema.safeParse(payload.data);
  if (!parsed.success) {
    throw new ApiError('The service returned data in an unexpected format.', payload.requestId);
  }
  return parsed.data;
}

const getJson = <S extends z.ZodType>(path: string, schema: S, tokens?: TokenSource) =>
  request('GET', path, schema, tokens);

/**
 * Authenticated binary download (e.g. an original PDF), with the same single token refresh
 * on 401 as JSON calls. Errors arrive as the usual JSON envelope.
 */
async function getBytes(
  path: string,
  tokens: TokenSource,
  method: Method = 'GET',
  body?: unknown,
): Promise<ArrayBuffer> {
  let response = await send(method, path, await tokens.getIdToken(), body);
  if (response.status === 401) {
    const fresh = await tokens.getIdToken(true).catch(() => null);
    if (fresh) response = await send(method, path, fresh, body);
  }
  if (response.ok) return response.arrayBuffer();
  let message = 'The document could not be loaded.';
  try {
    const payload = (await response.json()) as ApiResponse<unknown>;
    if ('error' in payload) message = payload.error.message;
  } catch {
    // Keep the generic message.
  }
  if (response.status === 401) throw new SessionEndedError(message);
  throw new ApiError(message, undefined, response.status);
}

const segment = (value: string) => encodeURIComponent(value);

const GrantResultSchema = z.object({ uid: z.string(), roles: z.array(RoleSchema) });
const RevokeResultSchema = z.object({ uid: z.string(), status: z.literal('revoked') });

export const api = {
  health: (): Promise<Health> => getJson('/api/v1/health', HealthSchema),
  featureFlags: (): Promise<FeatureFlags> => getJson('/api/v1/feature-flags', FeatureFlagsSchema),
  me: (tokens: TokenSource): Promise<Me> => getJson('/api/v1/me', MeSchema, tokens),
};

/** Platform administration (ADR-008). The server re-checks every call; the UI only guides. */
/** Legal quality review (ADR-032): Editorial console members. */
/** Received-law register and doctrine records (ADR-041), in the Editorial console. */
export const registerApi = {
  receivedLaw: (tokens: TokenSource) =>
    getJson('/api/v1/editorial/received-law', ReceivedLawListSchema, tokens),
  saveReceivedLaw: (tokens: TokenSource, input: ReceivedLawInput, id?: string) =>
    request(
      'POST',
      id ? `/api/v1/editorial/received-law/${segment(id)}` : '/api/v1/editorial/received-law',
      ReceivedLawRecordSchema,
      tokens,
      input,
    ),
  doctrines: (tokens: TokenSource) =>
    getJson('/api/v1/editorial/doctrines', DoctrineListSchema, tokens),
  saveDoctrine: (tokens: TokenSource, input: DoctrineInput, id?: string) =>
    request(
      'POST',
      id ? `/api/v1/editorial/doctrines/${segment(id)}` : '/api/v1/editorial/doctrines',
      DoctrineRecordSchema,
      tokens,
      input,
    ),
  decideDoctrine: (tokens: TokenSource, id: string, decision: 'approve' | 'retire') =>
    request(
      'POST',
      `/api/v1/editorial/doctrines/${segment(id)}/decision`,
      DoctrineRecordSchema,
      tokens,
      { decision },
    ),
};

export const qualityApi = {
  overview: (tokens: TokenSource): Promise<QualityOverview> =>
    getJson('/api/v1/quality', QualityOverviewSchema, tokens),
  createRun: (tokens: TokenSource, setId: string) =>
    request('POST', '/api/v1/quality/runs', z.object({ id: z.string() }), tokens, { setId }),
  run: (tokens: TokenSource, id: string): Promise<QualityRun> =>
    getJson(`/api/v1/quality/runs/${segment(id)}`, QualityRunSchema, tokens),
  generate: (tokens: TokenSource, runId: string, questionId: string) =>
    request(
      'POST',
      `/api/v1/quality/runs/${segment(runId)}/items/${segment(questionId)}/generate`,
      z.object({ questionId: z.string(), status: z.string() }),
      tokens,
    ),
  grade: (tokens: TokenSource, runId: string, questionId: string, grade: GradeRequest) =>
    request(
      'POST',
      `/api/v1/quality/runs/${segment(runId)}/items/${segment(questionId)}/grade`,
      z.object({ questionId: z.string(), graded: z.boolean() }),
      tokens,
      grade,
    ),
  signOff: (tokens: TokenSource, runId: string, statement: string) =>
    request(
      'POST',
      `/api/v1/quality/runs/${segment(runId)}/signoff`,
      z.object({ id: z.string() }).passthrough(),
      tokens,
      { statement },
    ),
  /** Lawyer-approved key points (ADR-035). */
  keyPoints: (tokens: TokenSource): Promise<KeyPointList> =>
    getJson('/api/v1/quality/keypoints', KeyPointListSchema, tokens),
  draftKeyPoints: (tokens: TokenSource, draft: DraftKeyPointsRequest) =>
    request('POST', '/api/v1/quality/keypoints', KeyPointSetSchema, tokens, draft),
  decideKeyPoints: (tokens: TokenSource, id: string, decision: 'approve' | 'retire') =>
    request(
      'POST',
      `/api/v1/quality/keypoints/${segment(id)}/${decision}`,
      KeyPointSetSchema,
      tokens,
    ),
};

/** The signed-in person's own data (ADR-031). */
export const meApi = {
  export: (tokens: TokenSource) =>
    getJson('/api/v1/me/export', z.object({ exportedAt: z.string() }).passthrough(), tokens),
};

/** Feedback and support requests (ADR-030). */
export const feedbackApi = {
  send: (tokens: TokenSource, body: CreateFeedbackRequest) =>
    request(
      'POST',
      '/api/v1/feedback',
      z.object({ id: z.string(), received: z.literal(true) }),
      tokens,
      body,
    ),
};

export const adminApi = {
  feedback: (tokens: TokenSource, status: 'open' | 'closed'): Promise<FeedbackList> =>
    getJson(`/api/v1/admin/feedback?status=${status}`, FeedbackListSchema, tokens),
  closeFeedback: (tokens: TokenSource, id: string, note: string): Promise<Feedback> =>
    request('POST', `/api/v1/admin/feedback/${segment(id)}/close`, FeedbackSchema, tokens, {
      note,
    }),
  /** Platform activity: counts and costs only (ADR-029). */
  operations: (tokens: TokenSource): Promise<OperationsReport> =>
    getJson('/api/v1/admin/operations', OperationsReportSchema, tokens),
  directory: (tokens: TokenSource): Promise<Directory> =>
    getJson('/api/v1/admin/directory', DirectorySchema, tokens),
  audit: (tokens: TokenSource): Promise<AuditEvent[]> =>
    getJson('/api/v1/admin/audit', z.array(AuditEventSchema), tokens),
  createOrganization: (
    tokens: TokenSource,
    body: CreateOrganizationRequest,
  ): Promise<Organization> =>
    request('POST', '/api/v1/admin/organizations', OrganizationSchema, tokens, body),
  createWorkspace: (tokens: TokenSource, body: CreateWorkspaceRequest): Promise<AdminWorkspace> =>
    request('POST', '/api/v1/admin/workspaces', AdminWorkspaceSchema, tokens, body),
  members: (tokens: TokenSource, workspaceId: string): Promise<WorkspaceMember[]> =>
    getJson(
      `/api/v1/admin/workspaces/${segment(workspaceId)}/members`,
      z.array(WorkspaceMemberSchema),
      tokens,
    ),
  grant: (tokens: TokenSource, workspaceId: string, body: GrantMembershipRequest) =>
    request(
      'POST',
      `/api/v1/admin/workspaces/${segment(workspaceId)}/members`,
      GrantResultSchema,
      tokens,
      body,
    ),
  revoke: (tokens: TokenSource, workspaceId: string, uid: string) =>
    request(
      'POST',
      `/api/v1/admin/workspaces/${segment(workspaceId)}/members/${segment(uid)}/revoke`,
      RevokeResultSchema,
      tokens,
    ),
};

/** Legal library (M3, ADR-012): extracted Acts, provisional and unverified. */
export const libraryApi = {
  /** Check the citations in a draft against the library (ADR-036); no AI, nothing stored. */
  citeCheck: (tokens: TokenSource, text: string): Promise<CiteCheckResult> =>
    request('POST', '/api/v1/library/citecheck', CiteCheckResultSchema, tokens, { text }),
  /** Judgments (ADR-018). */
  cases: (tokens: TokenSource): Promise<CaseCatalogue> =>
    getJson('/api/v1/library/cases', CaseCatalogueSchema, tokens),
  /** Judgments whose text discusses a word or phrase (ADR-034); no AI, nothing charged. */
  searchCases: (tokens: TokenSource, query: string): Promise<CaseSearch> =>
    getJson(
      `/api/v1/library/cases/search?q=${encodeURIComponent(query)}`,
      CaseSearchSchema,
      tokens,
    ),
  catalogue: (tokens: TokenSource): Promise<LibraryCatalogue> =>
    getJson('/api/v1/library/catalogue', LibraryCatalogueSchema, tokens),
  version: (tokens: TokenSource, versionId: string): Promise<LibraryVersion> =>
    getJson(`/api/v1/library/versions/${segment(versionId)}`, LibraryVersionSchema, tokens),
  /**
   * The original PDF, byte for byte. The server sends it under a neutral content type so
   * download managers do not seize it; it is re-labelled as a PDF here and checked to be one.
   */
  original: async (tokens: TokenSource, versionId: string): Promise<Blob> => {
    const bytes = await getBytes(`/api/v1/library/versions/${segment(versionId)}/original`, tokens);
    const magic = new TextDecoder().decode(new Uint8Array(bytes, 0, Math.min(5, bytes.byteLength)));
    if (magic !== '%PDF-') {
      throw new ApiError(
        'The document arrived empty or damaged. If a download manager (such as IDM) is installed, ' +
          'it may have intercepted it; try again or add this site to its exceptions.',
      );
    }
    return new Blob([bytes], { type: 'application/pdf' });
  },
  /** Editorial verdicts, without notes or reviewers (ADR-023). */
  reviews: (tokens: TokenSource): Promise<PublicReviewList> =>
    getJson('/api/v1/library/reviews', PublicReviewListSchema, tokens),
};

/** Research memos in a Professional workspace (ADR-024). */
/** Drafting (ADR-037): matters private to their author in the active workspace. */
export const draftingApi = {
  list: (tokens: TokenSource, ws: string): Promise<MatterList> =>
    getJson(`/api/v1/workspaces/${segment(ws)}/matters`, MatterListSchema, tokens),
  create: (tokens: TokenSource, ws: string, intake: MatterIntake): Promise<Matter> =>
    request('POST', `/api/v1/workspaces/${segment(ws)}/matters`, MatterSchema, tokens, intake),
  get: (tokens: TokenSource, ws: string, id: string): Promise<Matter> =>
    getJson(`/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}`, MatterSchema, tokens),
  /** Share with the firm workspace's lawyers, or stop sharing (ADR-042); author only. */
  share: (tokens: TokenSource, ws: string, id: string, shared: boolean): Promise<Matter> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/sharing`,
      MatterSchema,
      tokens,
      { shared },
    ),
  remove: (tokens: TokenSource, ws: string, id: string) =>
    request(
      'DELETE',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}`,
      z.object({ deleted: z.boolean() }),
      tokens,
    ),
  /** Analyse the matter and propose documents: one drafting task. */
  plan: (tokens: TokenSource, ws: string, id: string): Promise<Matter> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/plan`,
      MatterSchema,
      tokens,
    ),
  /** Draft one planned document: one drafting task. */
  draft: (
    tokens: TokenSource,
    ws: string,
    id: string,
    documentId: string,
    instruction?: string,
  ): Promise<Matter> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/drafts`,
      MatterSchema,
      tokens,
      { documentId, ...(instruction ? { instruction } : {}) },
    ),
  edit: (
    tokens: TokenSource,
    ws: string,
    id: string,
    draftId: string,
    body: string,
    baseVersion: number,
  ) =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/drafts/${segment(draftId)}/versions`,
      MatterSchema,
      tokens,
      { body, baseVersion },
    ),
  /** Ask for further work on a draft: one drafting task. */
  revise: (
    tokens: TokenSource,
    ws: string,
    id: string,
    draftId: string,
    instruction: string,
    baseVersion: number,
  ) =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/drafts/${segment(draftId)}/revisions`,
      MatterSchema,
      tokens,
      { instruction, baseVersion },
    ),
  approve: (tokens: TokenSource, ws: string, id: string, draftId: string, version: number) =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/drafts/${segment(draftId)}/approval`,
      MatterSchema,
      tokens,
      { version },
    ),
  exportDocx: async (
    tokens: TokenSource,
    ws: string,
    id: string,
    draftId: string,
  ): Promise<Blob> => {
    const bytes = await getBytes(
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/drafts/${segment(draftId)}/exports`,
      tokens,
      'POST',
    );
    const magic = new TextDecoder().decode(new Uint8Array(bytes, 0, Math.min(2, bytes.byteLength)));
    if (magic !== 'PK') throw new ApiError('The Word file arrived empty or damaged. Try again.');
    return new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
  },
  /** Add a filing package's documents to the plan (ADR-040); no AI. */
  addPackage: (tokens: TokenSource, ws: string, id: string, pkg: string): Promise<Matter> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/packages`,
      MatterSchema,
      tokens,
      { package: pkg },
    ),
  /** Client documents on a matter (ADR-044). */
  documents: (tokens: TokenSource, ws: string, id: string): Promise<MatterDocumentList> =>
    getJson(
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/documents`,
      MatterDocumentListSchema,
      tokens,
    ),
  uploadDocument: (
    tokens: TokenSource,
    ws: string,
    id: string,
    file: File,
  ): Promise<MatterDocument> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/documents`,
      MatterDocumentSchema,
      tokens,
      new RawBody(file, {
        'content-type': 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
      }),
    ),
  readDocument: (tokens: TokenSource, ws: string, id: string, doc: string) =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/documents/${segment(doc)}/read`,
      MatterDocumentSchema,
      tokens,
    ),
  removeDocument: (tokens: TokenSource, ws: string, id: string, doc: string) =>
    request(
      'DELETE',
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/documents/${segment(doc)}`,
      z.object({ deleted: z.boolean() }),
      tokens,
    ),
  downloadDocument: async (
    tokens: TokenSource,
    ws: string,
    id: string,
    doc: MatterDocument,
  ): Promise<Blob> => {
    const bytes = await getBytes(
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/documents/${segment(doc.id)}/file`,
      tokens,
    );
    return new Blob([bytes]);
  },
  /** The package's drafted documents as one zip of Word files. */
  exportPackage: async (
    tokens: TokenSource,
    ws: string,
    id: string,
    pkg: string,
  ): Promise<Blob> => {
    const bytes = await getBytes(
      `/api/v1/workspaces/${segment(ws)}/matters/${segment(id)}/packages/${segment(pkg)}/exports`,
      tokens,
      'POST',
    );
    const magic = new TextDecoder().decode(new Uint8Array(bytes, 0, Math.min(2, bytes.byteLength)));
    if (magic !== 'PK') throw new ApiError('The package arrived empty or damaged. Try again.');
    return new Blob([bytes], { type: 'application/zip' });
  },
};

export const memoApi = {
  list: (tokens: TokenSource, workspaceId: string): Promise<MemoList> =>
    getJson(`/api/v1/workspaces/${segment(workspaceId)}/memos`, MemoListSchema, tokens),
  get: (tokens: TokenSource, workspaceId: string, memoId: string): Promise<Memo> =>
    getJson(
      `/api/v1/workspaces/${segment(workspaceId)}/memos/${segment(memoId)}`,
      MemoSchema,
      tokens,
    ),
  create: (tokens: TokenSource, workspaceId: string, body: CreateMemoRequest): Promise<Memo> =>
    request('POST', `/api/v1/workspaces/${segment(workspaceId)}/memos`, MemoSchema, tokens, body),
  edit: (
    tokens: TokenSource,
    workspaceId: string,
    memoId: string,
    body: { body: string; expectedVersion: number },
  ): Promise<Memo> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(workspaceId)}/memos/${segment(memoId)}/versions`,
      MemoSchema,
      tokens,
      body,
    ),
  review: (
    tokens: TokenSource,
    workspaceId: string,
    memoId: string,
    version: number,
  ): Promise<Memo> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(workspaceId)}/memos/${segment(memoId)}/review`,
      MemoSchema,
      tokens,
      { version },
    ),
  share: (
    tokens: TokenSource,
    workspaceId: string,
    memoId: string,
    visibility: MemoVisibility,
  ): Promise<Memo> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(workspaceId)}/memos/${segment(memoId)}/visibility`,
      MemoSchema,
      tokens,
      { visibility },
    ),
  /** A Word copy of one version (ADR-025); the server records the export first. */
  exportDocx: async (
    tokens: TokenSource,
    workspaceId: string,
    memoId: string,
    version: number,
  ): Promise<Blob> => {
    const bytes = await getBytes(
      `/api/v1/workspaces/${segment(workspaceId)}/memos/${segment(memoId)}/exports`,
      tokens,
      'POST',
      { format: 'docx', version },
    );
    const magic = new TextDecoder().decode(new Uint8Array(bytes, 0, Math.min(2, bytes.byteLength)));
    if (magic !== 'PK') {
      throw new ApiError(
        'The Word file arrived empty or damaged. If a download manager (such as IDM) is installed, ' +
          'it may have intercepted it; try again or add this site to its exceptions.',
      );
    }
    return new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
  },
  /** Record that a print or PDF copy is being made in the browser (ADR-025). */
  recordPrint: (tokens: TokenSource, workspaceId: string, memoId: string, version: number) =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(workspaceId)}/memos/${segment(memoId)}/exports`,
      z.object({ recorded: z.literal(true) }),
      tokens,
      { format: 'print', version },
    ),
  remove: (tokens: TokenSource, workspaceId: string, memoId: string) =>
    request(
      'DELETE',
      `/api/v1/workspaces/${segment(workspaceId)}/memos/${segment(memoId)}`,
      z.object({ id: z.string(), deleted: z.boolean() }),
      tokens,
    ),
};

/** Report a problem with a document (ADR-026); any verified reader. */
export const reportApi = {
  create: (tokens: TokenSource, report: CreateSourceReportRequest) =>
    request(
      'POST',
      '/api/v1/legal/reports',
      z.object({ id: z.string(), received: z.literal(true) }),
      tokens,
      report,
    ),
};

/** Editorial review of the corpus (ADR-023): Editorial console members only. */
export const editorialApi = {
  /** Open reports, or every report on one document (ADR-026). */
  reports: (tokens: TokenSource, versionId?: string): Promise<SourceReportList> =>
    getJson(
      `/api/v1/editorial/reports${versionId ? `?versionId=${segment(versionId)}` : ''}`,
      SourceReportListSchema,
      tokens,
    ),
  resolveReport: (
    tokens: TokenSource,
    reportId: string,
    resolution: ResolveSourceReportRequest,
  ): Promise<SourceReport> =>
    request(
      'POST',
      `/api/v1/editorial/reports/${segment(reportId)}/resolve`,
      SourceReportSchema,
      tokens,
      resolution,
    ),
  reviews: (tokens: TokenSource): Promise<ReviewList> =>
    getJson('/api/v1/editorial/reviews', ReviewListSchema, tokens),
  review: (tokens: TokenSource, versionId: string): Promise<ReviewDetail> =>
    getJson(`/api/v1/editorial/reviews/${segment(versionId)}`, ReviewDetailSchema, tokens),
  decide: (
    tokens: TokenSource,
    versionId: string,
    decision: ReviewDecision,
  ): Promise<ReviewDetail> =>
    request(
      'POST',
      `/api/v1/editorial/reviews/${segment(versionId)}/decisions`,
      ReviewDetailSchema,
      tokens,
      decision,
    ),
};

/** Research documents (ADR-045): the caller's own, in a professional workspace. */
export const researchDocumentsApi = {
  list: (tokens: TokenSource, ws: string): Promise<ResearchDocumentList> =>
    getJson(
      `/api/v1/workspaces/${segment(ws)}/research-documents`,
      ResearchDocumentListSchema,
      tokens,
    ),
  upload: (tokens: TokenSource, ws: string, file: File): Promise<ResearchDocument> =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/research-documents`,
      ResearchDocumentSchema,
      tokens,
      new RawBody(file, {
        'content-type': 'application/octet-stream',
        'x-file-name': encodeURIComponent(file.name),
      }),
    ),
  read: (tokens: TokenSource, ws: string, id: string) =>
    request(
      'POST',
      `/api/v1/workspaces/${segment(ws)}/research-documents/${segment(id)}/read`,
      ResearchDocumentSchema,
      tokens,
    ),
  remove: (tokens: TokenSource, ws: string, id: string) =>
    request(
      'DELETE',
      `/api/v1/workspaces/${segment(ws)}/research-documents/${segment(id)}`,
      z.object({ deleted: z.boolean() }),
      tokens,
    ),
};

/** Ask Buakei over the Acts (ADR-017). The server limits each account to a few questions a day. */
export const assistantApi = {
  /** Ask a question; with a chatId it continues that chat (ADR-019). */
  ask: (
    tokens: TokenSource,
    question: string,
    chatId?: string,
    /** Research documents to read for this question (ADR-045). */
    documents?: { workspaceId: string; ids: string[] },
  ): Promise<AskAnswer> =>
    request('POST', '/api/v1/assistant/ask', AskAnswerSchema, tokens, {
      question,
      ...(chatId ? { chatId } : {}),
      ...(documents && documents.ids.length > 0 ? { documents } : {}),
    }),
  chats: (tokens: TokenSource): Promise<ChatList> =>
    getJson('/api/v1/assistant/chats', ChatListSchema, tokens),
  /** A case brief (ADR-020): served if already written, else written (uses a question). */
  /** A practice quiz on a topic, written from the library (ADR-021); uses a question. */
  quiz: (tokens: TokenSource, topic: string): Promise<LiveQuiz> =>
    request('POST', '/api/v1/assistant/quizzes', LiveQuizSchema, tokens, { topic }),
  brief: (tokens: TokenSource, versionId: string): Promise<CaseBrief> =>
    request('POST', `/api/v1/assistant/briefs/${segment(versionId)}`, CaseBriefSchema, tokens),
  chat: (tokens: TokenSource, id: string): Promise<Chat> =>
    getJson(`/api/v1/assistant/chats/${segment(id)}`, ChatSchema, tokens),
  deleteChat: (tokens: TokenSource, id: string) =>
    request('DELETE', `/api/v1/assistant/chats/${segment(id)}`, DeletedChatSchema, tokens),
  deleteAllChats: (tokens: TokenSource) =>
    request('DELETE', '/api/v1/assistant/chats', DeletedChatsSchema, tokens),
};

const DeletedChatSchema = z.object({ id: z.string(), deleted: z.literal(true) });
const DeletedChatsSchema = z.object({ deleted: z.number() });
